import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { RiCloseLine } from "@remixicon/react";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import GaneshDataGrid from "../DataGrid/GaneshDataGrid";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../hooks/useGaneshApi";
import { FulfillmentError_T } from "../../types/Shipping";

interface ShippingSummaryStatusDialogProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  dateStart: string;
  dateEnd: string;
}

const ShippingSummaryStatusDialog: React.FC<
  ShippingSummaryStatusDialogProps
> = ({ open, onClose, customerId, dateStart, dateEnd }) => {
  const { apiRequest } = useApi();

  const { data, isLoading } = useQuery({
    queryKey: ["fulfillment-errors", customerId, dateStart, dateEnd],
    queryFn: async () => {
      const params = new URLSearchParams({
        customerId,
        dateStart,
        dateEnd,
      });

      const response = await apiRequest(
        `/v1/fulfillments_analytics/errors?${params.toString()}`,
        {
          method: "GET",
        }
      );
      return response.data as FulfillmentError_T[];
    },
    enabled: open,
  });

  const columns = [
    {
      field: "delivery_id",
      headerName: "Delivery ID",
      width: 120,
    },
    {
      field: "delivery_name",
      headerName: "Delivery Name",
      width: 200,
    },
    {
      field: "delivery_fulfillment_id",
      headerName: "Event ID",
      width: 120,
    },
    {
      field: "target_fulfillment_date",
      headerName: "Fulfillment Date",
      width: 150,
    },
    { field: "status", headerName: "Status", width: 200, id: "status" },
    {
      field: "error_message",
      headerName: "Error Message",
      width: 510,
      flex: 1,
      wordWrap: "break-word",
      whiteSpace: "normal",
      overflow: "auto",
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "normal",
            wordWrap: "break-word",
            overflow: "visible",
            maxHeight: "none",
            lineHeight: "1.2",
            height: "100%",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "status_last_updated",
      headerName: "Last Updated At",
      width: 200,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="error-details-dialog"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Error Details:{" "}
        {!isLoading && data?.length
          ? `${data[0].customer_name} — ${customerId}`
          : customerId}
        <IconButton
          aria-label="close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <RiCloseLine />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ minHeight: "200px" }}>
          <GaneshDataGrid
            gridId="fulfillment-error-overview"
            data={data || []}
            columns={columns}
            loading={isLoading}
            headerFilters={false}
            customHeight="400px"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShippingSummaryStatusDialog;
